<template>
  <div class="flex-col flex gap-4">
    <div class="border-b border-linear-subtle pb-3 flex flex-col justify-start gap-2">
      <LabelReset
        id="add-on-form-reset-label"
        :initialValue="initialAddOn"
        :modelValue="modelValue"
        @update:modelValue="resetAddOnFormToInitialAddOn"
      >
        Add-On Update
      </LabelReset>
      <p class="text-fg-secondary text-sm">
        Changes to this Add-on <span class="font-semibold">only apply</span> to this Performance.
      </p>
    </div>
    <TextInput
      id="name-input"
      label="Name"
      placeholder="Name"
      class="mb-2"
      :initialValue="initialAddOnValues.name"
      v-model.trim="addOnForm.name"
    />
    <TextareaInput
      id="description-input"
      label="Description"
      placeholder="Description"
      class="mb-2"
      :initialValue="initialAddOnValues.description"
      v-model.trim="addOnForm.description"
    />
    <div class="flex justify-start items-start">
      <div class="w-1/2 pr-2">
        <NumberInput
          :initialValue="initialAddOnValues.price"
          v-model="addOnForm.price"
          id="price-input"
          label="Price"
          step=".01"
          min="0"
        />
      </div>
      <div class="pl-2 w-1/2">
        <NumberInput
          id="tax-rate-input"
          :initialValue="initialAddOnValues.tax_rate"
          v-model="addOnForm.tax_rate"
          label="Tax Rate"
          step=".01"
          min="0"
        />
      </div>
    </div>
    <div
      id="is-active-checkbox"
      class="flex flex-col items-start justify-start cursor-pointer mb-2"
      @click="addOnForm.is_active = !addOnForm.is_active"
    >
      <div class="flex items-center justify-start gap-2">
        <component
          :is="addOnForm.is_active ? EyeIcon : EyeSlashIcon"
          class="w-4 h-4 text-fg-secondary"
        />
        <LabelReset
          id="is-active-checkbox-label"
          :initialValue="initialAddOnValues.is_active"
          :modelValue="addOnForm.is_active"
          @update:modelValue.stop="addOnForm.is_active = $event"
        >
          {{ addOnForm.is_active ? 'Active' : 'Inactive' }}
        </LabelReset>
      </div>
      <p class="text-fg-secondary text-sm ml-6">
        Inactive Add-ons will not be available for purchase.
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import { computed, onMounted, ref, toRefs, watch } from 'vue'

import EyeIcon from '@/assets/eye.svg?component'
import EyeSlashIcon from '@/assets/eyeSlash.svg?component'
import LabelReset from '@/common/components/molecules/LabelReset.vue'
import NumberInput from '@/common/components/molecules/NumberInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import TextareaInput from '@/common/components/molecules/TextareaInput.vue'

import { PerformanceAddOnRead } from '@generated/types'

type AddOnForm = Omit<PerformanceAddOnRead, 'id' | 'parent_id'>

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:modelValue', value: PerformanceAddOnRead): void
}>()

const props = withDefaults(
  defineProps<{
    initialAddOn?: PerformanceAddOnRead
    modelValue?: PerformanceAddOnRead
  }>(),
  {
    initialAddOn: undefined,
    modelValue: undefined,
  },
)
const { initialAddOn, modelValue } = toRefs(props)

const addOnForm = ref<AddOnForm>({
  is_active: true,
  name: '',
  description: '',
  price: 0,
  tax_rate: 0,
})
onMounted(updateAddOnFormFromProps)
// When add-on fires changes externally, update the form
watch(modelValue, updateAddOnFormFromProps, { deep: true })
// When form fires changes, update the add-on
watch(
  addOnForm,
  newAddOn => {
    if (!modelValue.value) return
    const updatedAddOn: PerformanceAddOnRead = {
      ...modelValue.value,
      ...newAddOn,
      price: newAddOn.price.toFixed(2),
      tax_rate: newAddOn.tax_rate.toFixed(2),
    }
    if (_.isEqual(updatedAddOn, modelValue.value)) {
      return
    }
    emit('update:modelValue', updatedAddOn)
  },
  { deep: true },
)
function updateAddOnFormFromProps() {
  if (!modelValue.value) return
  addOnForm.value = {
    ...modelValue.value,
    price: Number(modelValue.value.price),
    tax_rate: Number(modelValue.value.tax_rate),
  }
}
function resetAddOnFormToInitialAddOn() {
  if (!initialAddOn.value) return
  addOnForm.value = {
    ...initialAddOn.value,
    price: Number(initialAddOn.value.price),
    tax_rate: Number(initialAddOn.value.tax_rate),
  }
}
const initialAddOnValues = computed<Partial<PerformanceAddOnRead>>(() => {
  if (!initialAddOn.value) return {}
  return {
    ...initialAddOn.value,
    price: Number(initialAddOn.value.price),
    tax_rate: Number(initialAddOn.value.tax_rate),
  }
})
</script>
